import React, {useMemo, useState} from "react";
import BasicText from "../../components/form-v2/basic-text";
import {GenderSelect, RealGenderSelect} from "../../components/form-v2/searchable-select/GenderSelect";
import {useUserActivation} from "./UserActivationContext";
import {LanguageSelect, LanguageSelectMultiple} from "../../components/form-v2/searchable-select/LanguageSelect";
import {NationalitySelect} from "../../components/form-v2/searchable-select/NationalitySelect";
import {CountrySelectSingle} from "../../components/form-v2/searchable-select/CountrySelectSingle";
import {BlueButton, OrangeButton} from "../../components/ui/Buttons";
import EmergencyContact from "../../features/user-management/pages/user-profile/components/emergency-contact";
import {PassportDetailsNoTitle} from "../../features/user-management/pages/user-profile/components/passport-details";
import {PhoneInput} from "../../components/form-v2/phone-input/PhoneInput";
import {MultiInputMultiField} from "../../components/form-v2/multi-input/MultiInputMultiField";

export const UserForm: React.FC = () => {
    const ctx = useUserActivation()
    const [showAdditional, setShowAdditional] = useState(false)

    return <>
        <div className="flex flex-col gap-8 w-full max-w-2xl">
            <PersonalDetails/>

            <button
                type="button"
                onClick={() => setShowAdditional(v => !v)}
                className="flex items-center gap-2 text-sm font-medium text-blue bg-transparent border-none cursor-pointer p-0 hover:opacity-70 transition-opacity"
            >
                <span className={`material-icons text-[18px] leading-none transition-transform ${showAdditional ? "rotate-180" : ""}`}>expand_more</span>
                {showAdditional ? "Hide additional profile information" : "Show additional profile information"}
            </button>

            {showAdditional && (
                <div className="flex flex-col gap-8">
                    <Demographic/>
                    <EmergencyContactForm/>
                    <PassportForm/>
                    <InsurancePolicyForm/>
                    <AdditionalContactInfo/>
                </div>
            )}
        </div>

        <SavingStatus/>

        <div className="flex flex-row gap-8 rounded-md w-full max-w-2xl">
            <div className="flex-1 flex flex-col gap-8">
                <BlueButton text={"Complete Activation"} onClick={ctx.submit} loading={ctx.saving}/>
            </div>
        </div>

    </>
}

const SavingStatus: React.FC = () => {
    const ctx = useUserActivation()

    if (ctx.savingError == "") {
        return null
    }

    return <div className={"bg-red text-white text-center rounded-md py-2 px-4 w-full md:w-2/3"}>
            {ctx.savingError}
    </div>

}


const AdditionalContactInfo: React.FC = () => {
    const ctx = useUserActivation()
    return <div className="bg-white shadow-md p-4 flex flex-col gap-4">
        <p className="text-md text-left pb-2 font-semibold">Additional Contact Information</p>
        <PhoneInput label="Personal Mobile" initialValue={ctx.personalMobilePhone} onChange={ctx.setPersonalMobilePhone}/>
        <PhoneInput label="Home Phone" initialValue={ctx.homePhone} onChange={ctx.setHomePhone}/>
        <BasicText type="email" label="Personal Email" value={ctx.personalEmail} onChange={ctx.setPersonalEmail}/>
    </div>
}


const PassportForm: React.FC = () => {
    const ctx = useUserActivation()

    return <div className="bg-white shadow-md p-4 flex flex-col gap-8">
        <MultiInputMultiField label={"Passport Details"}
                              initialItems={ctx.passports}
                              columns={2}
                              onChange={ctx.setPassports}
                              showLabels
                              placeholders={{
                                  number: "Passport Number",
                                  expiresAt: "Expires On",
                                  countryISO: "Country",
                              }}
                              types={{
                                  number: "text",
                                  expiresAt: "date",
                                  countryISO: "country",
                              }}
                              required={[
                                  "number",
                                  "countryISO"
                              ]}
        />
    </div>

}

const InsurancePolicyForm: React.FC = () => {
    const ctx = useUserActivation()

    return <div className="bg-white shadow-md p-4 flex flex-col gap-8">
        <MultiInputMultiField
            label={"Insurance Policies"}
            initialItems={ctx.insurancePolicies}
            onChange={ctx.setInsurancePolicies}
            showLabels={true}
            columns={2}
            placeholders={{
                insurer: "Insurer",
                policyNumber: "Policy Number",
                expiresAt: "Expires On"
            }}
            types={{
                expiresAt: "date",
            }}
            required={["insurer", "policyNumber"]}
        />
    </div>

}





const EmergencyContactForm: React.FC = () => {
    const ctx = useUserActivation()

    return <div className="bg-white shadow-md p-4 flex flex-col gap-8">
        <MultiInputMultiField label={"Emergency Contacts"} initialItems={ctx.emergencyContacts} columns={2}
                              showLabels
                              onChange={ctx.setEmergencyContacts}
                              placeholders={{
                                  firstName: "First Name",
                                  lastName: "Last Name",
                                  relationship: "Relationship",
                                  email: "Email",
                                  phoneNumber: "Phone Number"
                              }}
                              types={{
                                  phoneNumber: "phone",
                              }}
                              required={[
                                  "firstName",
                                  "lastName"
                              ]}
        />
    </div>

}


const PersonalDetails: React.FC = () => {
    const ctx = useUserActivation()

    return <div className="bg-white shadow-md p-4 flex flex-col gap-4">
        <p className="text-md text-left pb-2 font-semibold">Personal Details</p>
        <BasicText type={"email"} value={ctx.email} onChange={(e) => ctx.setEmail(e.toLowerCase())}
                   label="Email Address" disabled/>
        <BasicText value={ctx.firstName} onChange={ctx.setFirstName} label="First Name" required/>
        <BasicText value={ctx.middleName} onChange={ctx.setMiddleName} label="Middle Name/s"/>
        <BasicText value={ctx.lastName} onChange={ctx.setLastName} label="Last Name" required/>
        <BasicText type="date" value={ctx.dateOfBirth} onChange={ctx.setDateOfBirth} label="Date of Birth"/>
        {/* <RealGenderSelect gender={ctx.genderAtBirth} onChange={ctx.setGenderAtBirth}/>*/}
        <PhoneInput required initialValue={ctx.phone} onChange={ctx.setPhone} label="Mobile Phone"/>


    </div>
}

const Demographic: React.FC = () => {
    const ctx = useUserActivation()

    return <div className="bg-white shadow-md p-4 flex flex-col gap-4">
        <p className="text-md text-left pb-2 font-semibold">Demographic Details</p>
        <GenderSelect gender={ctx.gender} onChange={ctx.setGender}/>
        <div>
            <label className="mb-1">Main Language</label>
            <LanguageSelect value={ctx.mainLanguage} setValue={ctx.setMainLanguage}/>
        </div>

        <div>
            <label className="mb-1">Secondary Languages</label>
            <LanguageSelectMultiple value={ctx.secondaryLanguages} setValue={ctx.setSecondaryLanguages}/>
        </div>

        <div>
            <label className="mb-1">Nationality</label>
            <NationalitySelect value={ctx.nationality} setValue={ctx.setNationality}/>
        </div>

        <CountrySelectSingle value={ctx.countryISO} setValue={ctx.setCountryISO} label={"Country of Residence"}/>


    </div>

}